/**
 * m3 echo Frontend - Model Comparison metrics
 *
 * Shows the metrics of a comparison run: sample composition, error rates,
 * agreement of each arm with the stored analysis (informative only) and
 * between the arms, and tokens/time per arm. `null` means "not measured" and
 * is shown as "–", never as 0.
 */

import React from 'react';
import { Box, Card, CardContent, Grid, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import type { AgreementBlock, ComparisonRunResponse, UsageBlock } from '../../services/admin';

const dash = '–';

const formatPct = (value: number | null | undefined): string =>
  value === null || value === undefined ? dash : `${value.toFixed(1)} %`;

const formatNumber = (value: number | null | undefined, digits = 0): string =>
  value === null || value === undefined ? dash : value.toFixed(digits);

const AGREEMENT_ROWS: { key: keyof AgreementBlock; label: string }[] = [
  { key: 'score_equal_pct', label: 'Score gleich' },
  { key: 'mentioned_equal_pct', label: 'Nennung gleich' },
  { key: 'position_equal_pct', label: 'Position gleich' },
  { key: 'champion_equal_pct', label: 'Zuerst genannte Firma gleich' },
  { key: 'flip_true_to_false_pct', label: 'Nennung → keine' },
  { key: 'flip_false_to_true_pct', label: 'keine → Nennung' },
];

interface AgreementColumn {
  title: string;
  block: AgreementBlock | null;
}

const AgreementTable: React.FC<{ columns: AgreementColumn[] }> = ({ columns }) => (
  <Table size="small">
    <TableHead>
      <TableRow>
        <TableCell />
        {columns.map((column) => (
          <TableCell key={column.title} align="right">
            {column.title}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
    <TableBody>
      <TableRow>
        <TableCell>Paare</TableCell>
        {columns.map((column) => (
          <TableCell key={column.title} align="right">
            {column.block ? column.block.pairs : dash}
          </TableCell>
        ))}
      </TableRow>
      {AGREEMENT_ROWS.map((row) => (
        <TableRow key={row.key}>
          <TableCell>{row.label}</TableCell>
          {columns.map((column) => (
            <TableCell key={column.title} align="right">
              {column.block ? formatPct(column.block[row.key] as number | null) : dash}
            </TableCell>
          ))}
        </TableRow>
      ))}
    </TableBody>
  </Table>
);

const UsageCard: React.FC<{ title: string; usage: UsageBlock | null }> = ({ title, usage }) => (
  <Card variant="outlined">
    <CardContent>
      <Typography color="text.secondary" gutterBottom>
        {title}
      </Typography>
      <Typography variant="body2">
        Ø Tokens in / out: {formatNumber(usage?.avg_input_tokens)} / {formatNumber(usage?.avg_output_tokens)}
      </Typography>
      <Typography variant="body2">Ø Sekunden: {formatNumber(usage?.avg_seconds, 2)}</Typography>
      <Typography variant="caption" color="text.secondary">
        unvollständig gemessen: {formatPct(usage?.incomplete_pct)}
      </Typography>
    </CardContent>
  </Card>
);

export const ModelComparisonMetrics: React.FC<{ run: ComparisonRunResponse }> = ({ run }) => {
  const metrics = run.metrics;
  if (!metrics) return null;
  const hasArm2 = Boolean(run.comparison_model_2);
  const composition = metrics.composition;

  return (
    <Box sx={{ mb: 3 }}>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={4}>
          <Card variant="outlined">
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Stichprobe
              </Typography>
              <Typography variant="body2">
                ausgewählt {run.total_results} · verglichen {run.processed_count} · fehlgeschlagen{' '}
                {run.error_count ?? dash}
              </Typography>
              <Typography variant="body2">
                {composition.projects} Projekte · {composition.jobs} Jobs
              </Typography>
              <Typography variant="body2">
                verglichene mit Nennung {composition.baseline_mentioned} · ohne{' '}
                {composition.baseline_not_mentioned}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card variant="outlined">
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Fehlerquoten
              </Typography>
              <Typography variant="body2">Paare: {formatPct(metrics.pair_error_pct)}</Typography>
              <Typography variant="body2">{run.variant_1}: {formatPct(metrics.arm1_error_pct)}</Typography>
              {hasArm2 && (
                <Typography variant="body2">{run.variant_2}: {formatPct(metrics.arm2_error_pct)}</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <UsageCard title={run.variant_1} usage={metrics.arm1_usage} />
            {hasArm2 && <UsageCard title={run.variant_2 ?? ''} usage={metrics.arm2_usage} />}
          </Box>
        </Grid>
      </Grid>

      {hasArm2 && (
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Arm 1 ↔ Arm 2
            </Typography>
            <AgreementTable columns={[{ title: `${run.variant_1} ↔ ${run.variant_2}`, block: metrics.arm1_vs_arm2 }]} />
          </CardContent>
        </Card>
      )}

      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Gegen das gespeicherte Ergebnis (nur informativ)
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Die gespeicherte Analyse hat keine gesicherte Herkunft (Fallbacks, Neuanalysen); für
            Entscheidungen zählen nur frisch gerechnete Arme.
          </Typography>
          <AgreementTable
            columns={[
              { title: run.variant_1, block: metrics.arm1_vs_baseline },
              ...(hasArm2 ? [{ title: run.variant_2 ?? '', block: metrics.arm2_vs_baseline }] : []),
            ]}
          />
        </CardContent>
      </Card>
    </Box>
  );
};

export default ModelComparisonMetrics;
